import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";

/**
 * /rentals — city picker landing page.
 * Two big cinematic tiles (Edmonton first, Calgary second). Clicking
 * one lands the visitor on /residences with the correct city toggle
 * pre-selected via the ?city query param.
 */
export default function Rentals() {
  return (
    <main
      style={{ backgroundColor: "#F8F5EF", minHeight: "100vh" }}
      data-testid="rentals-city-picker"
    >
      {/* HEADER */}
      <section style={{ paddingTop: 132, paddingBottom: 32 }}>
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal>
            <Eyebrow color="#C89A4E">Homes for rent · Alberta</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h1
              className="font-display mt-6 max-w-[22ch]"
              style={{
                fontSize: "clamp(48px,7vw,88px)",
                color: "#201E1A",
                lineHeight: 1,
                letterSpacing: "-0.015em",
              }}
            >
              Pick your{" "}
              <span className="italic" style={{ color: "#C89A4E" }}>
                city.
              </span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              className="body-measure mt-6 text-[17px]"
              style={{ color: "rgba(32,30,26,0.72)" }}
            >
              Two cities, one small team, a short list of homes. Choose where
              you&rsquo;re looking and we&rsquo;ll take you to the neighbourhood
              map.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CITY TILES */}
      <section className="mx-auto max-w-[1440px] px-6 pb-32 pt-8 md:px-10">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
          <CityTile
            city="edmonton"
            title="Edmonton"
            tagline="Our home base — most of the current inventory sits here."
            note="11 neighbourhoods · live map · 1 building for rent"
            image="https://dcvqrrbrkrkzgjwxjgph.supabase.co/storage/v1/object/public/properties/cities/edmonton-skyline.jpg"
            featured
          />
          <CityTile
            city="calgary"
            title="Calgary"
            tagline="Four pockets we know inside out — new inventory coming soon."
            note="4 neighbourhoods · full write-ups coming"
            image="https://dcvqrrbrkrkzgjwxjgph.supabase.co/storage/v1/object/public/properties/cities/calgary-skyline.jpg"
          />
        </div>
      </section>
    </main>
  );
}

const CityTile = ({ city, title, tagline, note, image, featured }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
  >
    <Link
      to={`/residences?city=${city}`}
      className="group relative block overflow-hidden"
      style={{ borderRadius: 24, aspectRatio: "5 / 6" }}
      data-testid={`city-tile-${city}`}
    >
      <div
        className="absolute inset-0 transition-transform duration-[1200ms] group-hover:scale-[1.05]"
        style={{
          backgroundImage: `url(${image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(20,35,27,0.15) 0%, rgba(20,35,27,0.6) 60%, rgba(20,35,27,0.9) 100%)",
        }}
      />
      <div className="relative z-10 flex h-full flex-col justify-between p-8 md:p-10">
        <div className="flex items-start justify-between">
          {featured && (
            <span
              className="label-caps rounded-full px-3 py-1 text-[9px]"
              style={{
                backgroundColor: "#E3B268",
                color: "#14231B",
                letterSpacing: "0.24em",
              }}
            >
              Where we started
            </span>
          )}
          <span
            className="label-caps ml-auto text-[10px]"
            style={{ color: "rgba(237,233,224,0.7)" }}
          >
            {note}
          </span>
        </div>
        <div>
          <h2
            className="font-display"
            style={{
              fontSize: "clamp(48px,6vw,80px)",
              color: "#F8F5EF",
              lineHeight: 0.95,
              letterSpacing: "-0.02em",
            }}
          >
            {title}
          </h2>
          <p
            className="mt-3 max-w-[46ch] text-[15px]"
            style={{ color: "rgba(237,233,224,0.86)" }}
          >
            {tagline}
          </p>
          <span
            className="mt-6 inline-flex items-center gap-2 text-[13px]"
            style={{ color: "#E3B268" }}
          >
            <span className="label-caps">Explore the map</span>
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-1"
            />
          </span>
        </div>
      </div>
    </Link>
  </motion.div>
);
